<template>
  <svg
    class="area-brand"
    :class="{ 'area-brand--ink': ink }"
    :viewBox="`0 0 ${MARK_W} ${totalH}`"
    preserveAspectRatio="xMidYMid meet"
    role="img"
    :aria-label="`easy ${name}`"
  >
    <image class="area-brand__mark" href="/easy-mark.png" x="0" y="0" :width="MARK_W" :height="MARK_H" />
    <g :transform="nameTransform">
      <text ref="textEl" class="area-brand__name" x="0" y="0" dominant-baseline="hanging">{{ name }}</text>
    </g>
  </svg>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from 'vue'

/**
 * A marca de uma área, no mesmo lockup das artes originais.
 *
 * O desenho veio medido de public/EasyCRM.png: a marca "easy + palete" ocupa
 * 2606 × 550 px e o nome da ferramenta é desenhado logo abaixo, ocupando
 * exatamente a MESMA LARGURA da marca. É essa regra que faz "CRM" (3 letras) e
 * "SERVICE DESK" (12) ocuparem o mesmo bloco: o nome não tem corpo fixo, ele é
 * escalado até encostar nas duas bordas da marca.
 *
 * Consequência: a ALTURA do conjunto depende do nome — quanto menos letras,
 * mais o nome é ampliado e mais alto fica o bloco ("CRM" chega a 1,5× a altura
 * da marca). Por isso o componente é um SVG único com viewBox calculada, e não
 * uma pilha de elementos: quem usa define a ALTURA, a largura é que varia.
 * Dimensionar pela largura faria a marca vazar da barra justamente nos nomes
 * curtos.
 *
 * O "easy + palete" é a arte original recortada na tinta (public/easy-mark.png,
 * branca sobre transparente) e o nome é texto. Assim toda área nova ganha a
 * marca sem depender de um PNG novo, e as artes antigas — que tinham sobras de
 * transparência diferentes e por isso precisavam de alturas diferentes na
 * barra — passam a render do mesmo tamanho.
 */
const props = defineProps<{
  /** Nome da ferramenta, em caixa alta. Ex.: 'CRM', 'SERVICE DESK'. */
  name: string
  /** true desenha em preto (tema claro); padrão é branco (tema escuro). */
  ink?: boolean
}>()

const MARK_W = 2606
const MARK_H = 550
/** Respiro entre a marca e o nome, na mesma proporção do lockup original. */
const GAP = 20

const textEl = ref<SVGTextElement | null>(null)
// Proporção do 'CRM' original, para não haver salto antes da primeira medida.
const totalH = ref(MARK_H + GAP + 860)
const nameTransform = ref(`translate(0 ${MARK_H + GAP})`)

function fit() {
  const el = textEl.value
  if (!el) return
  try {
    const b = el.getBBox()
    if (!(b.width > 0) || !(b.height > 0)) return
    const k = MARK_W / b.width
    totalH.value = MARK_H + GAP + b.height * k
    // Encosta o canto superior esquerdo da caixa do texto em (0, abaixo da
    // marca). O deslocamento entra antes da escala, por isso o -b.x/-b.y * k.
    nameTransform.value = `translate(${-b.x * k} ${MARK_H + GAP - b.y * k}) scale(${k})`
  } catch {
    // getBBox lança se o nó ainda não foi desenhado; os valores iniciais seguram.
  }
}

onMounted(() => nextTick(fit))
watch(() => props.name, () => nextTick(fit))
</script>

<style scoped lang="scss">
.area-brand {
  display: block;
  // Quem usa manda a altura; a largura sai da viewBox.
  height: 100%;
  width: auto;
  user-select: none;

  &__name {
    font-family: Georgia, 'Times New Roman', serif;
    font-weight: 700;
    // As artes originais têm as capitulares espaçadas. Sem isso o nome fica
    // estreito, e como ele é escalado até a largura da marca, estreito vira
    // alto demais em relação ao "easy".
    letter-spacing: 0.1em;
    fill: #fff;
  }

  // A arte é branca; no tema claro ela vira preta. invert() sobre branco dá
  // preto exato e não toca na transparência, então não precisa de um segundo
  // arquivo só para mudar a cor. Só a imagem é invertida — o nome tem fill
  // próprio e seria desfeito junto.
  &--ink {
    .area-brand__mark {
      filter: invert(1);
    }
    .area-brand__name {
      fill: #000;
    }
  }
}
</style>
